<template>
  <v-container>
    <v-toolbar flat color="toolbar-action transparent">
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
        z-index="20"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            autocomplete="off"
            v-model="search.keyword"
            label=""
            placeholder="Search Organisations"
            prepend-inner-icon="mdi-magnify"
            filled
            rounded
            dense
            clearable
            v-bind="attrs"
            v-on="on"
            class="v-input--search"
            @click:clear="clearSearchAction"
            @keydown.enter="searchAction()"
          ></v-text-field>
        </template>
        <OrganisationSearchForm
          @clicked-search-button="clickedSearchButtonInModal"
          @clicked-clear-button="clearSearchAction"
          :meta="meta"
          :initialValues="getInitialFormValues()"
        />
      </v-menu>
      <v-spacer></v-spacer>
      <v-btn color="primary v-btn--new" dark class="ma-2" to="/organisations/new">New</v-btn>
    </v-toolbar>
    <Paginate :meta="meta" v-on:paginate="paginateAction" />
    <v-data-table
      :headers="headers"
      :items="organisations"
      :options.sync="options"
      @click:row="editItem"
      :loading="loading"
      loading-text="Loading... Please wait"
      hide-default-footer
    >
      <template v-slot:item.actions="{ item }">
        <v-spacer></v-spacer>
        <v-icon small @click.stop="deleteItem(item)">mdi-delete</v-icon>
      </template>
    </v-data-table>
    <ConfirmDialog
      title="Confirm delete"
      description="Are you sure you want to delete this item"
      okayLabel="Yes"
      cancelLabel="No"
      :show="showDeleteConfirmDialog"
      @confirm-dialog-clicked="confirmDelete"
    />
  </v-container>
</template>
<style scoped>
.v-input--search {
  max-width: 80%;
}
.v-btn--new {
  position: absolute;
  top: -12px;
  right: 0;
}
.v-menu__content {
  max-width: 640px;
  padding: 20px 20px 0 20px;
  background: white;
}
</style>
<script>
import { mapGetters } from 'vuex';
import store from '@/store/index';
import ConfirmDialog from '@/views/dialogs/ConfirmDialog';
import OrganisationSearchForm from '@/views/OrganisationSearchForm';
import Paginate from '@/views/Paginate';
import { dateMixin } from '@/mixins/dateMixin';

export default {
  name: 'OrganisationsView',
  components: {
    OrganisationSearchForm,
    Paginate,
    ConfirmDialog
  },
  mixins: [dateMixin],
  beforeRouteEnter(to, from, next) {
    const currentPage = parseInt(to.query.page) || 1;
    const sortBy = to.query.sort_by || 'title';
    const sortDesc = to.query.sort_desc === undefined ? false : to.query.sort_desc === 'true';
    const keyword = to.query.keyword || '';
    const active = to.query.active === 'true';

    if (keyword || active) {
      let searchParams = {
        page: currentPage,
        sort_by: sortBy,
        sort_desc: sortDesc,
        subtitle: true
      };

      if (keyword) {
        searchParams.keyword = keyword;
      } else {
        if (to.query.title) {
          searchParams.title = to.query.title;
        }
        if (to.query.suburb) {
          searchParams.suburb = to.query.suburb;
        }
        if (to.query.organisation_statuses_id) {
          searchParams.organisation_statuses_id = to.query.organisation_statuses_id;
        }
        if (to.query.organisation_types_id) {
          searchParams.organisation_types_id = to.query.organisation_types_id;
        }
        if (to.query.branch_id) {
          searchParams.branch_id = to.query.branch_id;
        }
      }
      store.dispatch('organisation/searchOrganisations', searchParams).then(() => {
        to.params.page = currentPage;
        next(vm => {
          if (keyword) {
            vm.search.keyword = keyword;
            vm.search.active = true;
            vm.search.subtitle = true;
          }
        });
      });
    } else {
      store
        .dispatch('organisation/list', {
          page: currentPage,
          sort_by: sortBy,
          sort_desc: sortDesc,
          subtitle: true
        })
        .then(() => {
          to.params.page = currentPage;
          next();
        });
    }
  },
  methods: {
    load(updateQuery = true) {
      const options = {
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'title',
        sort_desc: this.options.sortDesc[0] ?? false,
        subtitle: ''
      };

      if (updateQuery) {
        this.$router.push({
          query: options
        });
      }

      store.dispatch('organisation/list', options);
    },
    editItem(item) {
      this.$router.push({ path: `/organisations/${item.id}` });
    },
    deleteItem(item) {
      this.organisation_id = item.id;
      this.showDeleteConfirmDialog = true;
    },
    confirmDelete: function (deleteItem) {
      this.showDeleteConfirmDialog = false;
      if (deleteItem) {
        this.processDelete();
      }
    },
    async processDelete() {
      await store.dispatch('organisation/delete', this.organisation_id);
      this.options.page = parseInt(this.$route.query.page) || 1;
      this.load(false);
    },
    paginateAction(item, page) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: page
        }
      });

      if (item == 'next') {
        store.dispatch('organisation/paginate', this.links.next);
      } else if (item == 'previous') {
        store.dispatch('organisation/paginate', this.links.prev);
      }
    },
    clickedSearchButtonInModal(options) {
      this.menu = false;
      const defaultOptions = {
        active: true,
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'title',
        sort_desc: this.options.sortDesc[0] ?? false,
        subtitle: true
      };
      this.searchQuery({
        ...defaultOptions,
        ...options
      });
    },
    searchAction() {
      this.searchQuery({
        keyword: this.search.keyword,
        active: true,
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'title',
        sort_desc: this.options.sortDesc[0] ?? false,
        subtitle: true
      });
    },
    searchQuery(options) {
      Object.keys(options).forEach(key => {
        if (options[key] === null || options[key] === undefined) {
          delete options[key];
        }
      });

      store.dispatch('organisation/searchOrganisations', options);

      this.$router.push({
        query: options
      });

      this.search = options;
      this.menu = false;
    },
    clearSearchAction() {
      this.search = {
        keyword: '',
        active: false,
        subtitle: true
      };

      this.$router.push({
        query: {}
      });

      this.load(false);
    },
    getInitialFormValues() {
      const query = this.$route.query;
      return {
        title: query.title || null,
        suburb: query.suburb || null,
        organisation_statuses_id: query.organisation_statuses_id ? parseInt(query.organisation_statuses_id) : null,
        organisation_types_id: query.organisation_types_id ? parseInt(query.organisation_types_id) : null,
        branch_id: query.branch_id ? parseInt(query.branch_id) : null
      };
    }
  },
  computed: {
    ...mapGetters('organisation', ['loading', 'error', 'organisations', 'meta', 'links'])
  },
  watch: {
    options: {
      handler() {
        if (this.options.init) {
          if (this.search.active) {
            this.search.page = this.options.page;
            this.search.sort_by = this.options.sortBy[0] ?? 'title';
            this.search.sort_desc = this.options.sortDesc[0] ?? false;
            this.search.subtitle = true;
            this.searchQuery(this.search);
          } else {
            this.load();
          }
        } else {
          this.options.init = true;
        }
      },
      deep: true
    }
  },
  data: () => ({
    menu: false,
    search: {
      keyword: '',
      active: false,
      subtitle: true
    },
    showDeleteConfirmDialog: false,
    options: {},
    headers: [
      { text: 'Title', value: 'title' },
      { text: 'Status', value: 'status' },
      { text: 'Location', value: 'subtitle', sortable: false },
      { text: 'Type', value: 'type' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
    ]
  })
};
</script>
